import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
  FormControlLabel,
} from "@mui/material";
import { Edit as EditIcon } from "../icons";
import { Delete as DeleteIcon } from "../icons";
import { Save as SaveIcon } from "../icons";
import { Add as AddIcon } from "../icons";
import { Info as InfoIcon } from "../icons";
import { Storefront as StorefrontIcon } from "../icons";
import { PhotoCamera } from "../icons";
import { ShoppingCart as ShoppingCartIcon } from "../icons";
import { Dashboard as DashboardIcon } from "../icons";
import { getFirestore, collection, query, getDocs, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { auth, uploadImage } from "../firebase";
import { T } from "./Translate";
import StoresAppBar from "./stores/StoresAppBar";
import { brandColors } from "../theme";
import { geocodeAddress } from "../utils/geo";

// CELESTE se usa solo donde el fondo es fijo (los chips más abajo, siempre
// oscuros); en el resto de la tarjeta (fondo del tema) se usa el accent de
// marca, que se oscurece en Modo Claro para seguir siendo legible.
const CELESTE = "#00bfff";
const ACCENT = brandColors.accent;
const BASE_URL = "notiline.com/stores/";

// blob: URLs sólo son válidas en la pestaña que las creó; si quedaron
// guardadas en Firestore de una carga anterior, no deben usarse como src.
const safeLogoUrl = (url) => (url && !url.startsWith("blob:") ? url : undefined);

const MyStores = () => {
  const [stores, setStores] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ name: "", description: "", address: "", phone: "", logoUrl: "", slug: "" });
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });
  const [detailsStore, setDetailsStore] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (userAuth) => {
      if (userAuth) {
        setUser(userAuth);
        fetchStores(userAuth.uid);
      } else {
        navigate("/login");
      }
    });
    return unsubscribe;
  }, [navigate]);

  const fetchStores = async (uid) => {
    setLoading(true);
    try {
      const db = getFirestore();
      // Obtener todos los documentos de la colección 'stores' y filtrar por ownerId
      const q = query(collection(db, "stores"));
      const snap = await getDocs(q);
      // Filtrar por ownerId y asegurar que se use el id (nombre del doc) como fallback de nombre
      setStores(
        snap.docs
          .filter((d) => d.data().ownerId === uid)
          .map((d) => {
            const data = d.data();
            return {
              id: d.id,
              ...data,
              // Si no hay campo name, usar el id del documento como nombre
              name: data.name || d.id,
              slug: data.slug || d.id,
            };
          })
      );
    } catch (err) {
      setSnackbar({ open: true, message: <T>Error al cargar tiendas</T>, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (store) => {
    setEditingId(store.id);
    setEditData({
      name: store.name || "",
      description: store.description || "",
      address: store.address || "",
      phone: store.phone || "",
      logoUrl: store.logoUrl || "",
      slug: store.slug || "",
    });
  };

  const handleEditChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const handleSave = async (id) => {
    try {
      const db = getFirestore();
      const updates = { ...editData };
      // Si cambió la dirección, recalcular la ubicación en el mapa.
      const previous = stores.find((s) => s.id === id);
      if (editData.address && editData.address !== previous?.address) {
        const coords = await geocodeAddress(editData.address);
        if (coords) Object.assign(updates, coords);
      }
      await updateDoc(doc(db, "stores", id), updates);
      setEditingId(null);
      fetchStores(user.uid);
      setSnackbar({ open: true, message: <T>Tienda actualizada</T>, severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: <T>Error al guardar cambios</T>, severity: "error" });
    }
  };

  const handleDelete = async (id) => {
    setConfirmDelete({ open: true, id });
  };

  const confirmDeleteStore = async () => {
    try {
      const db = getFirestore();
      await deleteDoc(doc(db, "stores", confirmDelete.id));
      fetchStores(user.uid);
      setSnackbar({ open: true, message: <T>Tienda eliminada</T>, severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: <T>Error al eliminar tienda</T>, severity: "error" });
    } finally {
      setConfirmDelete({ open: false, id: null });
    }
  };

  const handleToggleActive = async (store) => {
    try {
      const db = getFirestore();
      await updateDoc(doc(db, "stores", store.id), { active: !store.active });
      fetchStores(user.uid);
      setSnackbar({ open: true, message: store.active ? <T>Tienda desactivada</T> : <T>Tienda activada</T>, severity: "info" });
    } catch (err) {
      setSnackbar({ open: true, message: <T>Error al cambiar estado</T>, severity: "error" });
    }
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const url = await uploadImage(file);
      setEditData((prev) => ({ ...prev, logoUrl: url }));
    } catch (err) {
      setSnackbar({ open: true, message: <T>No pudimos subir la imagen</T>, severity: "error" });
    }
  };

  return (
    <Box>
      <StoresAppBar title={<T>Mis Tiendas</T>} subtitle={<T>Gestiona tus enlaces activos</T>} maxWidth="md" />
      <Box sx={{ maxWidth: { xs: "100%", md: "980px" }, mx: "auto", mt: 4, p: 2, boxSizing: "border-box" }}>
        {loading ? (
          <CircularProgress />
        ) : (
          <>
            {stores.map((store) => (
              <Card
                key={store.id}
                sx={{
                  mb: 2,
                  background: brandColors.backgroundPaper,
                  border: `1px solid ${brandColors.divider}`,
                  boxShadow: 3,
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                    <Avatar src={safeLogoUrl(store.logoUrl)} sx={{ bgcolor: CELESTE, width: 48, height: 48, flexShrink: 0 }}>
                      <StorefrontIcon />
                    </Avatar>
                    <Box>
                      {/* Mostrar nombre de la tienda en celeste, grande */}
                      <Typography variant="h5" sx={{ color: ACCENT, fontWeight: 700, mb: 0.5, wordBreak: "break-word" }}>{store.name}</Typography>
                      {/* Mostrar descripción debajo del nombre */}
                      <Typography variant="body2" sx={{ color: ACCENT, mb: 1, wordBreak: "break-word" }}>{store.description}</Typography>
                      {store.slug && (
                        <Typography variant="body2" sx={{ color: ACCENT, fontWeight: 500, wordBreak: "break-all" }}>
                          <a href={`https://${BASE_URL}${store.slug}`} target="_blank" rel="noopener noreferrer" style={{ color: ACCENT, textDecoration: "underline" }}>
                            {BASE_URL}{store.slug}
                          </a>
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {editingId === store.id ? (
                    <>
                      <TextField
                        label={<T>Nombre</T>}
                        name="name"
                        value={editData.name}
                        onChange={handleEditChange}
                        fullWidth
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        label={<T>Descripción</T>}
                        name="description"
                        value={editData.description}
                        onChange={handleEditChange}
                        fullWidth
                        multiline
                        minRows={2}
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        label={<T>Dirección</T>}
                        name="address"
                        value={editData.address}
                        onChange={handleEditChange}
                        fullWidth
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        label={<T>Teléfono</T>}
                        name="phone"
                        value={editData.phone}
                        onChange={handleEditChange}
                        fullWidth
                        sx={{ mb: 1 }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Button
                          variant="contained"
                          component="label"
                          startIcon={<PhotoCamera />}
                          sx={{ mr: 2 }}
                        >
                          <T>Subir Logo</T>
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={handleLogoUpload}
                          />
                        </Button>
                        {safeLogoUrl(editData.logoUrl) && (
                          <Avatar src={safeLogoUrl(editData.logoUrl)} sx={{ width: 40, height: 40, bgcolor: CELESTE, ml: 1 }} />
                        )}
                      </Box>
                      <TextField
                        label={<T>Slug (URL)</T>}
                        name="slug"
                        value={editData.slug}
                        onChange={handleEditChange}
                        fullWidth
                        sx={{ mb: 1 }}
                        helperText={<T>La parte final de la URL de la tienda.</T>}
                      />
                    </>
                  ) : (
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                      <Chip
                        label={store.active ? <T>Activa</T> : <T>Inactiva</T>}
                        sx={{ background: store.active ? CELESTE : "#5a5a5a", color: store.active ? "#222" : "#fff" }}
                        size="small"
                      />
                      {store.address && (
                        <Chip label={store.address} sx={{ background: "rgba(255,255,255,0.1)", color: CELESTE }} size="small" />
                      )}
                      {store.phone && (
                        <Chip label={store.phone} sx={{ background: "rgba(255,255,255,0.1)", color: CELESTE }} size="small" />
                      )}
                      {store.createdAt && (
                        <Chip
                          label={
                            new Date(store.createdAt.seconds * 1000).toLocaleDateString()
                          }
                          sx={{ background: "rgba(255,255,255,0.1)", color: CELESTE }}
                          size="small"
                        />
                      )}
                      <Tooltip title={<T>Ver detalles</T>}>
                        <IconButton sx={{ color: ACCENT }} onClick={() => setDetailsStore(store)}>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!store.active}
                            onChange={() => handleToggleActive(store)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: ACCENT,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: ACCENT,
                              },
                            }}
                          />
                        }
                        label={<span style={{ color: ACCENT, fontWeight: 500 }}><T>Activar tienda</T></span>}
                        labelPlacement="end"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  )}
                </CardContent>
                <CardActions sx={{ flexWrap: "wrap" }}>
                  {editingId === store.id ? (
                    <>
                      <Button
                        startIcon={<SaveIcon />}
                        onClick={() => handleSave(store.id)}
                        color="primary"
                        variant="contained"
                      >
                        <T>Guardar</T>
                      </Button>
                      <Button
                        onClick={() => setEditingId(null)}
                        color="inherit"
                        variant="outlined"
                      >
                        <T>Descartar cambios</T>
                      </Button>
                    </>
                  ) : (
                    <IconButton onClick={() => handleEdit(store)} sx={{ color: ACCENT }}>
                      <EditIcon />
                    </IconButton>
                  )}
                  <IconButton onClick={() => handleDelete(store.id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                  <Button
                    startIcon={<ShoppingCartIcon />}
                    sx={{ color: ACCENT, fontWeight: 600 }}
                    onClick={() => navigate(`/stores/${store.slug}`, { state: { tab: "productos" } })}
                  >
                    <T>Subir Productos</T>
                  </Button>
                  <Button
                    startIcon={<DashboardIcon />}
                    sx={{ color: ACCENT, fontWeight: 600 }}
                    onClick={() => navigate(`/stores/${store.slug}`, { state: { tab: "panel" } })}
                  >
                    <T>Panel de control</T>
                  </Button>
                </CardActions>
              </Card>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() => navigate("/stores/create")}
              sx={{ color: ACCENT, borderColor: ACCENT, mb: 2 }}
              variant="outlined"
            >
              <T>Agregar Tienda</T>
            </Button>
          </>
        )}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
        <Dialog open={!!detailsStore} onClose={() => setDetailsStore(null)} fullWidth maxWidth="xs">
          <DialogTitle sx={{ color: ACCENT, fontWeight: 700 }}>{detailsStore?.name}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
              {detailsStore?.description && (
                <Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}><T>Descripción</T></Typography>
                  <Typography variant="body2">{detailsStore.description}</Typography>
                </Box>
              )}
              {detailsStore?.address && (
                <Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}><T>Dirección</T></Typography>
                  <Typography variant="body2">{detailsStore.address}</Typography>
                </Box>
              )}
              {detailsStore?.phone && (
                <Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}><T>Teléfono</T></Typography>
                  <Typography variant="body2">{detailsStore.phone}</Typography>
                </Box>
              )}
              {detailsStore?.slug && (
                <Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}><T>Enlace</T></Typography>
                  <Typography variant="body2" sx={{ wordBreak: "break-all" }}>{BASE_URL}{detailsStore.slug}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary" }}><T>Estado</T></Typography>
                <Typography variant="body2">{detailsStore?.active ? <T>Activa</T> : <T>Inactiva</T>}</Typography>
              </Box>
              {detailsStore?.createdAt && (
                <Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}><T>Creada el</T></Typography>
                  <Typography variant="body2">{new Date(detailsStore.createdAt.seconds * 1000).toLocaleDateString()}</Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsStore(null)}><T>Cerrar</T></Button>
          </DialogActions>
        </Dialog>
        <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, id: null })}>
          <DialogTitle><T>¿Eliminar tienda?</T></DialogTitle>
          <DialogContent>
            <DialogContentText><T>Esta acción no se puede deshacer.</T></DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDelete({ open: false, id: null })}><T>Cancelar</T></Button>
            <Button onClick={confirmDeleteStore} color="error"><T>Eliminar</T></Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default MyStores;
